import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { api } from '@/api'
import type { AuthUser } from '@/api'
import {
  UNAUTHORIZED_EVENT,
  clearToken,
  getToken,
  isUnauthorizedFor,
  setToken,
} from '@/api/session'

/**
 * 当前**会员**账号（工作台那一侧）。
 *
 * ### 🔴 为什么这里不再有 `isAdmin`
 *
 * 2026-10-07 拆表之前，「是不是管理员」是 `user.role` 的一个值，于是顶栏靠它
 * 决定要不要显示后台入口。拆表之后**会员就是会员** —— 后台账号在另一张表、
 * 走另一套登录（`/api/admin/auth/*`），这个 Context 里拿到的任何东西都
 * 回答不了「能不能进后台」。
 *
 * 所以后台是**另一个 Context**（`AdminAuthContext`），两个 Context 各自
 * 对应一个 token。把 `isAdmin` 留在这里（比如永远返回 false）是最坏的一种：
 * 下一个读代码的人会照着它写分支，而那些分支永远不会成立。
 *
 * ### 三件必须在这里做的事
 *
 * 1. **进站先验一次会话**（`GET /api/auth/session`）：token 过期、账号被停用
 *    都会在这里变成 401，界面直接回登录页 —— 而不是让每个业务接口零零散散
 *    各报一句「登录已过期」，用户不知道该做什么。
 * 2. **后端说 401 就地掉线**：任何请求收到 401，`client.ts` 会广播
 *    `hw:unauthorized`（**带身份**），这里只认 `user` 那条。
 *    ⚠️ 必须按身份过滤 —— 不过滤的话，后台 token 过期会把正在干活的工作台
 *    一起踢下线，而用户看到的是一次毫无理由的掉线。
 * 3. **余额是活的**：`refresh()` 重新拉一次 session 拿最新额度。跑完任务后调它，
 *    否则顶栏的余额永远停在进站那一刻的数字上。
 *
 * ### 为什么 token 本身不放 state
 *
 * token 只存在 `api/session.ts` 那一处（localStorage）。这里有状态的是
 * **账号信息**，token 只是「能不能问出账号」的凭据。两处都存 token 的话，
 * 退出登录时漏清一处，就会出现「界面回登录页了，但请求还是带着旧 token」。
 */

type AuthApi = {
  /** 当前会员账号。null = 未登录（或登录态已失效）。 */
  user: AuthUser | null
  /** 首次进入还在验会话。true 时不要渲染「未登录」——那会闪一下登录页。 */
  booting: boolean
  /**
   * 会员登录，**并把刚登上的那个账号返回给调用方**。
   *
   * 为什么要返回：`setUser` 是异步的，调用方在同一轮里读 `user` 拿到的还是 null，
   * 而登录页要在这一轮就决定「回哪一页」（`location.state.from`）。
   */
  login: (account: string, password: string) => Promise<AuthUser>
  logout: () => Promise<void>
  /** 重新拉当前账号（余额变了就调它）。失败不抛错，静默保留旧值。 */
  refresh: () => Promise<void>
}

const Ctx = createContext<AuthApi | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  // 初值给 true：**刷新页面时不能先闪一下登录页**。没有 token 时
  // 下面那个 effect 会立刻把它置回 false（同步判断，不等待网络）。
  const [booting, setBooting] = useState(true)

  useEffect(() => {
    let alive = true
    const token = getToken()
    if (!token) {
      // 本地没有 token —— 不必问后端，直接就结束引导（省一次注定 401 的请求）
      setBooting(false)
      return
    }
    api
      .session()
      .then((u) => {
        if (alive) setUser(u)
      })
      .catch(() => {
        // 401（过期/停用）在这里吞掉：`client.ts` 已经清了 token 并播了广播，
        // 这边保持 user=null 即可。不弹错 —— 「需要重新登录」不是错误，
        // 是正常状态，登录页本身就是提示。
        if (alive) setUser(null)
      })
      .finally(() => {
        if (alive) setBooting(false)
      })
    return () => {
      alive = false
    }
  }, [])

  useEffect(() => {
    // 只认「会员侧」的失效广播 —— 见上面第 2 条。
    const onUnauthorized = (e: Event) => {
      if (isUnauthorizedFor(e, 'user')) setUser(null)
    }
    window.addEventListener(UNAUTHORIZED_EVENT, onUnauthorized)
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, onUnauthorized)
  }, [])

  const login = useCallback(async (account: string, password: string) => {
    // ⚠️ 先问后端（`api.login` 不改任何全局状态），拿到结果再写 token：
    // 顺序反了的话，密码错一次也会在 localStorage 里留下一个垃圾 token。
    const res = await api.login(account, password)
    setToken(res.token)
    setUser(res.user)
    return res.user
  }, [])

  const logout = useCallback(async () => {
    try {
      // 服务端要真吊销 token（不只是本地删掉）—— 否则这个 token 还能用 30 天。
      await api.logout()
    } catch {
      // 后端挂了 / 已过期：本地照样要退出。卡在这里不让人登录是最糟的结果。
    }
    clearToken()
    setUser(null)
  }, [])

  const refresh = useCallback(async () => {
    if (!getToken()) return
    try {
      setUser(await api.session())
    } catch {
      /* 静默：401 已由 broadcast 处理，其它错误保留旧值即可 */
    }
  }, [])

  const value = useMemo<AuthApi>(
    () => ({ user, booting, login, logout, refresh }),
    [user, booting, login, logout, refresh],
  )

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useAuth() {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
